import { useState } from "react";

import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import FilterListRoundedIcon from "@mui/icons-material/FilterListRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import {
  Box,
  IconButton,
  InputBase,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
} from "@mui/material";

const channelFilterOptions = [
  { label: "Todos canais", value: "all" },
  { label: "WhatsApp", value: "whatsapp_api" },
  { label: "SMS", value: "sms" },
  { label: "E-mail", value: "email" },
];

function QuickFilterButton({ active, children, onClick }) {
  return (
    <Box
      component="button"
      onClick={onClick}
      type="button"
      sx={(theme) => ({
        appearance: "none",
        backgroundColor: active ? theme.palette.primary.main : "transparent",
        border: 0,
        borderRadius: "999px",
        color: active ? theme.palette.primary.contrastText : theme.palette.text.secondary,
        cursor: "pointer",
        font: "inherit",
        fontSize: 12,
        fontWeight: active ? 800 : 600,
        lineHeight: 1,
        m: 0,
        minHeight: 28,
        px: 1.45,
        whiteSpace: "nowrap",
        "&:hover": {
          backgroundColor: active
            ? theme.palette.primary.main
            : theme.palette.action.hover,
        },
      })}
    >
      {children}
    </Box>
  );
}

function SelectionMenuItem({ active, children, onClick }) {
  return (
    <MenuItem
      onClick={onClick}
      sx={{
        fontSize: 13,
        gap: 1,
        minHeight: 36,
      }}
    >
      <Box
        sx={{
          alignItems: "center",
          display: "inline-flex",
          flexShrink: 0,
          height: 18,
          justifyContent: "center",
          width: 18,
        }}
      >
        {active ? <CheckRoundedIcon sx={{ color: "primary.main", fontSize: 17 }} /> : null}
      </Box>
      {children}
    </MenuItem>
  );
}

export default function MessageHistoryFilters({
  channelFilter,
  categoryFilter,
  onChannelFilterChange,
  onCategoryFilterChange,
  onSearchTermChange,
  searchOpen,
  searchTerm,
  setSearchOpen,
}) {
  const [channelAnchor, setChannelAnchor] = useState(null);
  const channelMenuOpen = Boolean(channelAnchor);
  const activeChannel = channelFilterOptions.find((option) => option.value === channelFilter);
  const searchPlaceholder = categoryFilter === "general" ? "Buscar histórico" : "Buscar campanha";

  return (
    <Box
      sx={{
        borderBottom: "1px solid",
        borderColor: "border.default",
        px: 1.25,
        py: 0.9,
      }}
    >
      <Stack alignItems="center" direction="row" spacing={0.75}>
        <Stack direction="row" spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
          <QuickFilterButton
            active={categoryFilter === "campaigns"}
            onClick={() => onCategoryFilterChange("campaigns")}
          >
            Campanhas
          </QuickFilterButton>
          <QuickFilterButton
            active={categoryFilter === "general"}
            onClick={() => onCategoryFilterChange("general")}
          >
            Geral
          </QuickFilterButton>
        </Stack>

        <Tooltip title="Buscar envio">
          <IconButton
            aria-label="Buscar envio"
            onClick={() => setSearchOpen((current) => !current)}
            size="small"
            sx={{
              color: searchOpen || searchTerm ? "primary.main" : "text.secondary",
              height: 30,
              width: 30,
            }}
          >
            <SearchRoundedIcon sx={{ fontSize: 19 }} />
          </IconButton>
        </Tooltip>

        <Tooltip title={`Canal: ${activeChannel?.label || "Todos canais"}`}>
          <IconButton
            aria-controls={channelMenuOpen ? "contact-message-channel-menu" : undefined}
            aria-haspopup="true"
            aria-label="Filtrar canal"
            onClick={(event) => setChannelAnchor(event.currentTarget)}
            size="small"
            sx={{
              color: channelFilter !== "all" ? "primary.main" : "text.secondary",
              height: 30,
              width: 30,
            }}
          >
            <FilterListRoundedIcon sx={{ fontSize: 19 }} />
          </IconButton>
        </Tooltip>
      </Stack>

      {searchOpen ? (
        <Box
          sx={{
            alignItems: "center",
            border: "1px solid",
            borderColor: "border.default",
            borderRadius: "8px",
            display: "flex",
            gap: 0.75,
            mt: 1,
            px: 1,
            py: 0.25,
          }}
        >
          <SearchRoundedIcon sx={{ color: "text.secondary", fontSize: 18 }} />
          <InputBase
            autoFocus
            fullWidth
            onChange={(event) => onSearchTermChange(event.target.value)}
            placeholder={searchPlaceholder}
            sx={{ fontSize: 13, minHeight: 30 }}
            value={searchTerm}
          />
        </Box>
      ) : null}

      <Menu
        anchorEl={channelAnchor}
        id="contact-message-channel-menu"
        onClose={() => setChannelAnchor(null)}
        open={channelMenuOpen}
        PaperProps={{
          sx: { borderRadius: "8px", minWidth: 190 },
        }}
      >
        {channelFilterOptions.map((option) => (
          <SelectionMenuItem
            active={channelFilter === option.value}
            key={option.value}
            onClick={() => {
              onChannelFilterChange(option.value);
              setChannelAnchor(null);
            }}
          >
            {option.label}
          </SelectionMenuItem>
        ))}
      </Menu>
    </Box>
  );
}
